/**
 * Generic recurring-poll driver with exponential backoff and a pause/resume
 * switch for visibility — framework-agnostic (no React, no `document`) so it
 * can drive both a component's useEffect-based poll (UploadDetailPanel) and a
 * plain callback-based poll (UploadHistoryContext's pollUploadStatus, which
 * is a useCallback, not a hook, and can't use another hook internally).
 *
 * Why backoff matters here specifically: this app's local dev setup routes
 * through a free ngrok tunnel that starts dropping/resetting requests under
 * sustained load (see UploadDetailPanel.tsx's RECORDS_POLL_MS docstring). A
 * failed poll retried instantly at the same rate hits a struggling tunnel at
 * full volume precisely when it's least able to take it — backing off gives
 * it room to recover instead of compounding the problem.
 *
 * Why pause/resume rather than tearing the timer down and rebuilding it:
 * visibility flips (alt-tab, minimize) are frequent and transient — routing
 * them through the same effect-dependency machinery that already caused the
 * cache-churn bug (a stable value changing identity tears down and rebuilds
 * the whole poll effect, including its listeners) is exactly the failure
 * mode to avoid. pause()/resume() mutate this poller's own internal state
 * without touching the effect that created it.
 */

export interface BackoffPollerOptions {
  /** Delay before the first poll, and the delay used again after any
   *  successful poll — the "healthy" cadence. */
  intervalMs: number;
  /** Ceiling for the exponentially-growing delay after consecutive
   *  failures — without this a persistent outage would back off forever
   *  toward an unusably long delay. */
  maxIntervalMs: number;
  /** One poll attempt. Resolve normally for "the request reached the
   *  server and got an answer" — including an answer that isn't done yet
   *  (e.g. status still "processing"); that's not a failure, it's expected
   *  and should keep polling at the normal cadence. Reject only for "this
   *  attempt to reach the server itself failed" (network error, timeout,
   *  thrown exception) — that's what should trigger backoff. Call
   *  `handle.stop()` from inside `poll` once a terminal condition is
   *  reached (there is nothing generic this module can know about what
   *  "terminal" means for a given caller).
   */
  poll: () => Promise<void>;
}

export interface BackoffPollerHandle {
  /** Permanently ends this poller — no further polls, ever. Idempotent. */
  stop: () => void;
  /** Suspends polling: clears any pending timer and, if a poll happened to
   *  be in flight when this was called, prevents that poll's own
   *  completion from scheduling another one. No network activity occurs
   *  while paused. Idempotent. */
  pause: () => void;
  /** Resumes a paused poller with an immediate poll (a "catch up now"
   *  fetch) rather than waiting out whatever delay was pending before the
   *  pause — the whole point of pausing on a hidden tab is to skip wasted
   *  polls, not to skip the first real one once it's visible again.
   *  No-ops if not currently paused, or if already stopped. */
  resume: () => void;
}

/** Computes the delay after N consecutive failures (0 = about to make the
 *  first/next attempt after a success, i.e. the normal cadence). Doubles
 *  per failure, capped at maxIntervalMs: intervalMs, 2x, 4x, 8x, ... cap. */
export function computeBackoffDelayMs(consecutiveFailures: number, intervalMs: number, maxIntervalMs: number): number {
  if (consecutiveFailures <= 0) return intervalMs;
  return Math.min(intervalMs * 2 ** consecutiveFailures, maxIntervalMs);
}

export function startBackoffPolling(options: BackoffPollerOptions): BackoffPollerHandle {
  const { intervalMs, maxIntervalMs } = options;
  let stopped = false;
  let paused = false;
  let busy = false;
  let consecutiveFailures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const scheduleNext = (delay: number) => {
    if (stopped || paused) return;
    timer = setTimeout(runOnce, delay);
  };

  const runOnce = async () => {
    timer = undefined;
    if (stopped || paused) return;
    busy = true;
    try {
      await options.poll();
      consecutiveFailures = 0;
    } catch {
      consecutiveFailures += 1;
    } finally {
      busy = false;
    }
    if (stopped || paused) return;
    scheduleNext(computeBackoffDelayMs(consecutiveFailures, intervalMs, maxIntervalMs));
  };

  scheduleNext(intervalMs);

  return {
    stop: () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
    pause: () => {
      if (stopped || paused) return;
      paused = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
    resume: () => {
      if (stopped || !paused) return;
      paused = false;
      // If a poll from before the pause is still in flight, let it finish
      // naturally — its own completion will schedule the next one at the
      // normal cadence now that paused is false again. Kicking off a
      // second concurrent poll on top of it isn't worth the complexity for
      // what's a rare, brief overlap window.
      if (!busy) runOnce();
    },
  };
}
